import { LegalPlaceholder } from "@/components/layout/LegalPlaceholder";

/**
 * /privacy-policy - Whistll Privacy Policy (last updated April 2026).
 * Copy transcribed from ~/Documents/Whistll/whistll-all-documents/
 * whistll-privacy-policy.pdf. Keep in sync with that PDF when legal
 * updates anything.
 */
export function PrivacyPolicyPage() {
  return (
    <LegalPlaceholder title="Privacy Policy" lastUpdated="April 2026">
      <h2>1. Introduction</h2>
      <p>
        Whistll ("we", "us", or "our") is committed to protecting your
        personal data and respecting your privacy. This Privacy Policy
        explains what data we collect, why we collect it, how we use it, and
        your rights in relation to it.
      </p>
      <p>
        This Policy applies to all users of the Whistll mobile application
        and related services (the "App") across the United Kingdom, United
        States, Canada, Australia, and New Zealand.
      </p>

      <h2>2. Who we are</h2>
      <p>Whistll is the data controller responsible for your personal data.</p>
      <p>
        <strong>Email:</strong>{" "}
        <a href="mailto:hello@whistll.com" className="underline underline-offset-2">
          hello@whistll.com
        </a>
        <br />
        <strong>Address:</strong> 71-75 Shelton Street, Covent Garden,
        London, WC2H 9JQ
        <br />
        <strong>Company number:</strong> 17172530
      </p>
      <p>
        <strong>UK users:</strong> We are registered with the Information
        Commission (formerly the Information Commissioner's Office) under
        registration number ZC130786.
      </p>
      <p>
        <strong>Quebec users:</strong> In accordance with Quebec's Law 25,
        our designated Privacy Officer is Uwaifiokun Osasumwen Osagie (
        <a href="mailto:hello@whistll.com" className="underline underline-offset-2">
          hello@whistll.com
        </a>
        ).
      </p>

      <h2>3. Data we collect</h2>

      <h3>3.1 Name &amp; contact details</h3>
      <ul>
        <li>
          Full name, email address, phone number, and any other profile
          information you provide.
        </li>
      </ul>

      <h3>3.2 Location data</h3>
      <ul>
        <li>
          General location (city or region) to help you find nearby games
          and courts.
        </li>
        <li>
          Precise GPS location, where you expressly grant permission, to
          show real-time results near you.
        </li>
        <li>
          You can manage or withdraw location permissions at any time
          through your device settings.
        </li>
      </ul>

      <h3>3.3 Photos, videos &amp; identifiable imagery</h3>
      <ul>
        <li>Profile pictures you choose to upload.</li>
        <li>
          Images and video clips you share within the App, including game
          footage, highlights, and team clips.
        </li>
        <li>
          Such content may include identifiable images of you and other
          individuals present during games.
        </li>
      </ul>

      <h3>3.4 Biometric data considerations</h3>
      <ul>
        <li>
          Game footage may contain facial imagery. Whistll does not
          currently perform facial recognition on user-uploaded content.
          Content is screened using automated content moderation (including
          AWS Rekognition) for unsafe content only, not for identification
          of individuals.
        </li>
        <li>
          Where applicable laws (including Illinois BIPA and other US state
          biometric privacy laws) apply, we take additional steps to ensure
          compliance.
        </li>
      </ul>

      <h3>3.5 Communications &amp; direct messages</h3>
      <ul>
        <li>Content of direct messages between users.</li>
        <li>
          Messages or correspondence you send to us, including support
          requests and feedback.
        </li>
        <li>
          Content posted in match rooms, team pages, or other community
          spaces.
        </li>
      </ul>

      <h3>3.6 Reliability, flags &amp; moderation data</h3>
      <ul>
        <li>Records of attendance, cancellations, and no-shows.</li>
        <li>Flags issued to your account and associated reasons.</li>
        <li>Ban history and reliability score.</li>
        <li>Moderation decisions relating to content you have uploaded.</li>
        <li>Reports you have made or that have been made against you.</li>
      </ul>

      <h3>3.7 Device &amp; usage data</h3>
      <ul>
        <li>Device type, operating system, and unique device identifiers.</li>
        <li>
          App usage data including features accessed, pages viewed, and
          time spent in the App.
        </li>
        <li>Log data including IP address, crash reports, and diagnostic information.</li>
      </ul>

      <h3>3.8 Booking &amp; payment data</h3>
      <ul>
        <li>
          Details of bookings made through the App, including date, time,
          venue, and game participants.
        </li>
        <li>
          Payment transaction records. We do not store full card details;
          these are handled securely by our third-party payment processor
          (Stripe).
        </li>
      </ul>

      <h3>3.9 Competitive &amp; social data</h3>
      <ul>
        <li>
          Team memberships, captaincy records, challenge history, league
          participation, and win/loss records.
        </li>
        <li>Follow relationships, blocks, and social activity.</li>
        <li>Man of the match votes and court ratings.</li>
      </ul>

      <h2>4. How we use your data</h2>
      <div className="not-prose overflow-hidden rounded-xl border border-border">
        <table className="w-full text-sm">
          <thead className="bg-secondary/40 text-xs uppercase tracking-wider text-muted-foreground">
            <tr>
              <th className="px-4 py-2 text-left">Purpose</th>
              <th className="px-4 py-2 text-left">Legal basis (UK)</th>
              <th className="px-4 py-2 text-left">Legal basis (US)</th>
            </tr>
          </thead>
          <tbody className="text-muted-foreground">
            <Row p="Creating and managing your account" uk="Contract" us="Necessary for service" />
            <Row p="Processing bookings and payments" uk="Contract" us="Necessary for service" />
            <Row p="Showing you nearby games and courts" uk="Legitimate interests / Consent" us="Service functionality" />
            <Row p="Enabling your profile and User Content" uk="Contract" us="Necessary for service" />
            <Row p="Content moderation and platform safety" uk="Recognised legitimate interests (UK DUAA 2025)" us="Legal compliance" />
            <Row p="Reliability tracking, flags, and ban enforcement" uk="Legitimate interests" us="Platform integrity" />
            <Row p="Direct messaging and social features" uk="Contract" us="Necessary for service" />
            <Row p="Push notifications (see section 12)" uk="Consent" us="Consent" />
            <Row p="Improving the App and fixing issues" uk="Legitimate interests" us="Service improvement" />
            <Row p="Fraud prevention and security" uk="Recognised legitimate interests (UK DUAA 2025)" us="Legal compliance" />
            <Row p="Complying with legal obligations" uk="Legal obligation" us="Legal compliance" />
          </tbody>
        </table>
      </div>
      <p>
        <strong>We will not sell your personal data to third parties.</strong>
      </p>

      <h2>5. Sharing your data</h2>

      <h3>5.1 Venue providers</h3>
      <p>
        When you make a booking, we share limited personal data with the
        relevant venue provider where necessary to facilitate the booking.
        Venue providers act as independent data controllers in respect of
        the data they receive. Categories of data shared with venues: name,
        email address, phone number (where provided), booking details
        (date, time, venue), and payment confirmation (not full payment
        details). We do not share precise location data, GPS tracking, or
        social data with venue providers.
      </p>

      <h3>5.2 Payment processors</h3>
      <p>
        Payment details are processed by Stripe. We share only the data
        necessary to complete your transaction. Full card details are never
        stored by Whistll. For venue payouts, Stripe Connect is used and
        venue bank details are handled directly by Stripe.
      </p>

      <h3>5.3 Content moderation providers</h3>
      <p>
        User-uploaded images and videos are processed through automated
        content moderation tools, including AWS Rekognition. Content is
        analysed for policy violations only.
      </p>

      <h3>5.4 Error tracking providers</h3>
      <p>
        Crash and error data is processed through tools such as Sentry to
        diagnose issues and improve the App.
      </p>

      <h3>5.5 Other users</h3>
      <p>Certain information is visible to other users by design, including:</p>
      <ul>
        <li>Your display name and profile picture</li>
        <li>Public activity (games joined, teams, league rankings)</li>
        <li>Content you choose to post publicly</li>
        <li>Reliability score (visibility may be limited)</li>
      </ul>
      <p>
        You control additional visibility (messaging, free agent visibility,
        etc.) through your account settings.
      </p>

      <h3>5.6 Legal &amp; regulatory disclosure</h3>
      <p>
        We may disclose data to law enforcement, regulators, or other
        authorities where legally required or where necessary to protect
        the rights, safety, or property of Whistll or its users.
      </p>

      <h3>5.7 Business transfers</h3>
      <p>
        In the event of a merger, acquisition, or sale of all or part of
        our business, your personal data may be transferred. We will notify
        you before your data is transferred and becomes subject to a
        different privacy policy.
      </p>
      <p>
        <strong>
          We do not sell, rent, or trade your personal data for marketing
          or commercial purposes.
        </strong>
      </p>

      <h2>6. Data retention</h2>
      <ul>
        <li>
          <strong>Account data</strong> - retained for the duration of your
          account and up to 2 years after closure.
        </li>
        <li>
          <strong>Booking records</strong> - up to 7 years for financial
          and legal compliance.
        </li>
        <li>
          <strong>Usage and device data</strong> - typically up to 12 months.
        </li>
        <li>
          <strong>Direct messages</strong> - retained for the duration of
          the conversation and up to 12 months after account closure or
          conversation deletion, whichever is longer.
        </li>
        <li>
          <strong>Content moderation records</strong> - retained for up to
          3 years for audit and appeal purposes, including records of
          removed content.
        </li>
        <li>
          <strong>Removed or reported content</strong> - where content is
          removed following a request, we may retain records of removal
          (including limited content metadata) for compliance and audit
          purposes for up to 3 years.
        </li>
        <li>
          <strong>Flags, bans, and reliability records</strong> - retained
          for the duration of your account and up to 2 years after closure
          to prevent circumvention.
        </li>
      </ul>
      <p>You may request deletion of your data at any time (see Section 9).</p>

      <h2>7. Data security</h2>
      <p>
        We take appropriate technical and organisational measures to
        protect your personal data, including encryption in transit, secure
        storage, and access controls. No method of electronic transmission
        or storage is 100% secure. We encourage you to use a strong, unique
        password. If we become aware of a data breach likely to affect your
        rights and freedoms, we will notify you and relevant regulatory
        authorities as required by applicable law.
      </p>

      <h2>8. International data transfers</h2>
      <p>
        Whistll operates across the United Kingdom, United States, Canada,
        Australia, and New Zealand. Your data may be transferred to and
        processed in countries outside your own.
      </p>
      <p>
        <strong>UK users:</strong> Where we transfer data outside the UK,
        we ensure appropriate safeguards are in place in accordance with UK
        GDPR and the Data (Use and Access) Act 2025, such as standard
        contractual clauses or adequacy decisions.
        <br />
        <strong>US users:</strong> Your data may be stored or processed
        outside your state or country. We take steps to ensure your data is
        protected wherever held.
        <br />
        <strong>Canadian users:</strong> Transfers are handled in
        accordance with PIPEDA and applicable provincial legislation.
        <br />
        <strong>Australian users:</strong> Transfers are handled in
        accordance with the Australian Privacy Principles under the Privacy
        Act 1988.
        <br />
        <strong>New Zealand users:</strong> Transfers are handled in
        accordance with the New Zealand Privacy Act 2020, including the
        Information Privacy Principles.
      </p>

      <h2>9. Your rights</h2>

      <h3>9.1 UK users</h3>
      <p>
        Under the UK GDPR and the Data Protection Act 2018, as updated by
        the Data (Use and Access) Act 2025, you have the following rights:
      </p>
      <ul>
        <li><strong>Right of access</strong> - to request a copy of the personal data we hold about you</li>
        <li><strong>Right to rectification</strong> - to correct inaccurate or incomplete data</li>
        <li><strong>Right to erasure</strong> - to request deletion in certain circumstances</li>
        <li><strong>Right to restrict processing</strong> - to limit how we use your data</li>
        <li><strong>Right to data portability</strong> - to receive your data in a structured, machine-readable format</li>
        <li><strong>Right to object</strong> - to object to processing based on legitimate interests</li>
        <li><strong>Right to withdraw consent</strong> - where processing is based on consent</li>
        <li>
          <strong>Right to complain directly to us</strong> - under the
          DUAA 2025, you now have a statutory right to complain directly to
          Whistll. We will acknowledge and respond within 30 days.
        </li>
      </ul>
      <p>
        To exercise any of these rights, contact{" "}
        <a href="mailto:hello@whistll.com" className="underline underline-offset-2">
          hello@whistll.com
        </a>
        . You also have the right to lodge a complaint with the Information
        Commission at{" "}
        <a href="https://www.ico.org.uk" target="_blank" rel="noopener noreferrer" className="underline underline-offset-2">
          www.ico.org.uk
        </a>{" "}
        or 0303 123 1113.
      </p>

      <h3>9.2 California residents (CCPA / CPRA)</h3>
      <ul>
        <li><strong>Right to know</strong> - details of personal data collected, used, disclosed, and shared</li>
        <li><strong>Right to delete</strong> - subject to certain exceptions</li>
        <li><strong>Right to correct</strong> - inaccurate personal data</li>
        <li><strong>Right to opt out of sale or sharing</strong> - we do not sell or share your personal data for cross-context behavioural advertising</li>
        <li><strong>Right to limit use of sensitive personal information</strong> - including precise geolocation</li>
        <li><strong>Right to non-discrimination</strong></li>
      </ul>
      <p>
        To submit a California privacy request, contact{" "}
        <a href="mailto:hello@whistll.com" className="underline underline-offset-2">
          hello@whistll.com
        </a>{" "}
        with the subject line "California Privacy Request". We will respond
        within 45 days, with a possible 45-day extension.
      </p>

      <h3>9.3 Maryland residents</h3>
      <p>
        Under the Maryland Online Data Privacy Act (MODPA), you have rights
        to access, correct, delete, and obtain a portable copy of your
        personal data, and to opt out of processing for targeted advertising
        or profiling. Whistll treats location data and sensitive personal
        information with heightened care and collects only the minimum data
        necessary.
      </p>

      <h3>9.4 Other US state residents</h3>
      <p>
        Users in Virginia, Colorado, Connecticut, Oregon, Texas, Montana,
        Iowa, Indiana, Tennessee, New Jersey, Delaware, Minnesota, New
        Hampshire, and other states with applicable privacy laws have
        similar rights including access, correction, deletion, and opt-out.
      </p>
      <p>
        In Colorado, California, Connecticut, and Oregon, we honour Global
        Privacy Control (GPC) signals as opt-out requests for data sharing
        for advertising purposes.
      </p>
      <p>
        To exercise any state privacy rights, contact{" "}
        <a href="mailto:hello@whistll.com" className="underline underline-offset-2">
          hello@whistll.com
        </a>
        .
      </p>

      <h3>9.5 Canadian residents</h3>
      <p>
        Under PIPEDA and applicable provincial legislation (including
        Quebec's Law 25), you have rights of access, correction, and
        withdrawal of consent.
      </p>

      <h3>9.6 Australian residents</h3>
      <p>
        Under the Australian Privacy Act 1988 and the Australian Privacy
        Principles, you have rights to access, correct, and complain about
        the handling of your personal information. Contact{" "}
        <a href="mailto:hello@whistll.com" className="underline underline-offset-2">
          hello@whistll.com
        </a>
        , and if unresolved, you may lodge a complaint with the Office of
        the Australian Information Commissioner.
      </p>

      <h3>9.7 New Zealand residents</h3>
      <p>Under the New Zealand Privacy Act 2020, you have the following rights:</p>
      <ul>
        <li><strong>Right to access</strong> your personal information held by Whistll</li>
        <li><strong>Right to request correction</strong> of inaccurate personal information</li>
        <li><strong>Right to complain</strong> to Whistll if you believe your privacy rights have been breached</li>
        <li>
          <strong>Right to lodge a complaint</strong> with the Office of
          the Privacy Commissioner at{" "}
          <a href="https://privacy.org.nz" target="_blank" rel="noopener noreferrer" className="underline underline-offset-2">
            privacy.org.nz
          </a>
        </li>
      </ul>
      <p>
        Whistll will respond to access or correction requests within 20
        working days as required under the Privacy Act 2020. Where we
        become aware of a privacy breach that is likely to cause serious
        harm, we will notify you and the Office of the Privacy Commissioner
        in accordance with the Act's breach notification requirements.
      </p>

      <h2>10. UK-specific compliance - Data (Use and Access) Act 2025</h2>
      <h3>10.1 Recognised legitimate interests</h3>
      <p>
        The DUAA introduces a lawful basis of "recognised legitimate
        interests" for certain processing including crime prevention and
        safeguarding. Whistll may rely on this basis, particularly for
        fraud prevention and platform security, without a full balancing
        test.
      </p>
      <h3>10.2 Cookies</h3>
      <p>
        From 5 February 2026, analytics cookies used solely to collect
        aggregate statistics to improve a service no longer require prior
        consent. All other non-essential cookies continue to require
        consent.
      </p>
      <h3>10.3 Cookie compliance</h3>
      <p>
        PECR penalties are aligned with UK GDPR levels. Our consent
        mechanisms are designed to give genuine and equal choice, with
        reject options as prominent as accept options.
      </p>
      <h3>10.4 Complaints handling</h3>
      <p>
        Under the DUAA, you have a statutory right to complain directly to
        controllers. Contact{" "}
        <a href="mailto:hello@whistll.com" className="underline underline-offset-2">
          hello@whistll.com
        </a>
        . We will acknowledge promptly and respond within 30 days. If you
        remain dissatisfied, you may escalate to the Information
        Commission.
      </p>
      <h3>10.5 Automated decision-making</h3>
      <p>
        Where Whistll uses automated processing that significantly affects
        you (including automated content moderation decisions), you have
        the right to request human review. Contact{" "}
        <a href="mailto:hello@whistll.com" className="underline underline-offset-2">
          hello@whistll.com
        </a>
        .
      </p>

      <h2>11. Children's privacy</h2>
      <p>
        Whistll is strictly for users aged 18 and over. We do not knowingly
        collect personal data from anyone under 18. If you believe a minor
        has created an account, contact us at{" "}
        <a href="mailto:hello@whistll.com" className="underline underline-offset-2">
          hello@whistll.com
        </a>{" "}
        and we will take steps to delete the information and close the
        account.
      </p>
      <ul>
        <li><strong>US users:</strong> We comply with the Children's Online Privacy Protection Act (COPPA).</li>
        <li><strong>UK users:</strong> We comply with the ICO's Children's Code and the DUAA's provisions on children's higher protection matters.</li>
      </ul>

      <h2>12. Push notifications &amp; communications</h2>
      <h3>12.1 Push notifications</h3>
      <p>With your consent, we send push notifications covering:</p>
      <ul>
        <li>Game reminders and confirmations</li>
        <li>New messages and follows</li>
        <li>Payment confirmations and refunds</li>
        <li>Match invitations and team challenges</li>
        <li>Marketing and promotions (optional, separately consented)</li>
      </ul>
      <p>
        You can manage or disable any category of notification in the App
        settings or your device settings.
      </p>
      <h3>12.2 Transactional emails</h3>
      <p>
        We send transactional emails for bookings, payments, account
        activity, and other service-essential communications. These cannot
        be opted out of while you maintain an active account.
      </p>
      <h3>12.3 Marketing emails</h3>
      <p>
        Marketing emails are opt-in and can be unsubscribed from at any
        time via the unsubscribe link in any marketing email or through
        your account settings.
      </p>

      <h2>13. Cookies &amp; tracking</h2>
      <p>
        The Whistll App and website use cookies and similar tracking
        technologies.{" "}
        <strong>UK users:</strong> In line with the updated PECR rules
        under the DUAA 2025, analytics cookies used purely to improve the
        App do not require prior consent. Other non-essential cookies
        require express consent.{" "}
        <strong>US users:</strong> You may opt out of certain tracking via
        device settings, the Global Privacy Control signal, or by
        contacting{" "}
        <a href="mailto:hello@whistll.com" className="underline underline-offset-2">
          hello@whistll.com
        </a>
        . See our full Cookie Policy for detail.
      </p>

      <h2>14. Third-party links &amp; services</h2>
      <p>
        The App may contain links to or integrations with third-party
        services, including venue providers and booking platforms. These
        parties operate independently of Whistll. You are encouraged to
        review the privacy policies of any third-party services you access
        through Whistll.
      </p>

      <h2>15. Changes to this policy</h2>
      <p>
        We may update this Privacy Policy from time to time. Where material
        changes are made, we will notify you via the App or email. The
        "Last updated" date reflects the most recent version. Continued use
        constitutes acceptance.
      </p>

      <h2>16. Contact us</h2>
      <p>
        <strong>Email:</strong>{" "}
        <a href="mailto:hello@whistll.com" className="underline underline-offset-2">
          hello@whistll.com
        </a>
        <br />
        <strong>Address:</strong> 71-75 Shelton Street, Covent Garden,
        London, WC2H 9JQ
      </p>
      <p>
        UK users may also contact the Information Commission at{" "}
        <a href="https://www.ico.org.uk" target="_blank" rel="noopener noreferrer" className="underline underline-offset-2">
          www.ico.org.uk
        </a>{" "}
        or 0303 123 1113. US users may contact the relevant state Attorney
        General's office if they believe their privacy rights have been
        violated. Canadian users may contact the Office of the Privacy
        Commissioner of Canada. Australian users may contact the Office of
        the Australian Information Commissioner. New Zealand users may
        contact the Office of the Privacy Commissioner at{" "}
        <a href="https://privacy.org.nz" target="_blank" rel="noopener noreferrer" className="underline underline-offset-2">
          privacy.org.nz
        </a>
        .
      </p>
    </LegalPlaceholder>
  );
}

function Row({ p, uk, us }: { p: string; uk: string; us: string }) {
  return (
    <tr className="border-t border-border">
      <td className="px-4 py-2 align-top">{p}</td>
      <td className="px-4 py-2 align-top">{uk}</td>
      <td className="px-4 py-2 align-top">{us}</td>
    </tr>
  );
}
